import react, { useEffect, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import "./MetlinkReportComponent.css";
import * as MetlinkService from "../../Services/Metlink/MetlinkService";
import { ChevronDown, Loader2, RefreshCcw } from "lucide-react";
import CommonContainer from "../Container/CommonContainer";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import useRightClickDisable from "../../Utils/disableCopyAndDevTools";
import { useRef } from "react";
import { FaRegFileAlt } from "react-icons/fa";

const MetlinkReportComponent = () => {
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  // const [showReportForm, setShowReportForm] = useState(false);
  const [mtdAchievementData, setMtdAchievementData] = useState("");
  const [monthlyAchievementData, setMonthlyAchievementData] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useRightClickDisable();

  const filteredPartners = partnerList.filter(partner =>
    partner.partnerCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

const handlePartnerSelect = (partner) => {
    setSelectedPartner(partner);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsDropdownOpen(true);

    const exactMatch = partnerList.find(
      p => p.partnerCode.toLowerCase() === value.toLowerCase()
    );
    
    if (exactMatch) {
      setSelectedPartner(exactMatch);
    } else if (value === '') {
      setSelectedPartner(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsDropdownOpen(true);
    } else if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const formattedYear = parseInt(year.split("-")[0], 10);
  const formattedMonth = parseInt(month, 10);

  useEffect(() => {
    MetlinkService.GetPartnerList()
      .then(({ data }) => {
        if (data.IsSuccess) {
          const partnerData = data.Data.map((item) => ({
            partnerCode: item.Partner_Code,
            didNumber: item.DIDNumber,
          }));
          setPartnerList(partnerData);
        } else {
          toast.error(data.ResponseMessage);
        }
      })
      .catch(() => {
        toast.error("Something went wrong.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleAchievementDetails = () => {
    if (!selectedPartner.partnerCode || !year || !month) {
      toast.error("Please select Partner, Year, and Month first.");
      return;
    }

    setLoading(true);
    setDataLoaded(false);

    MetlinkService.GetMTDAchievementDetails(
      selectedPartner.partnerCode,
      formattedMonth,
      formattedYear
    )
      .then(({ data }) => {
        if (data.IsSuccess) {
          setMtdAchievementData(data.Data);
          const extractedDay = new Date(data.Data.Month).getDate();
          setDay(extractedDay);
          setDataLoaded(true);
        } else {
          // toast.error(data.ResponseMessage);
        }
      })
      .catch(() => {
        toast.error("Something went wrong.");
      })
      .finally(() => {
        setLoading(false);
      });

    MetlinkService.GetMonthlyAchievementDetails(
      selectedPartner.partnerCode,
      formattedMonth,
      formattedYear
    )
      .then(({ data }) => {
        if (data.IsSuccess) {
          setMonthlyAchievementData(data.Data);
          const extractedDay = new Date(data.Data.Month).getDate();
          setDay(extractedDay);
          setDataLoaded(true);
        } else {
          toast.error(data.ResponseMessage);
        }
      })
      .catch(() => {
        toast.error("Something went wrong.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedPartner?.partnerCode && year && month) {
      handleAchievementDetails();
    }
  }, [selectedPartner, year, month]);

  const performanceData = [
    {
      name: "Revenue",
      Target: monthlyAchievementData?.Overall_Revenue_Target || 0,
      Achieved: monthlyAchievementData?.Overall_Achieved || 0,
    },
    {
      name: "Wellness",
      Target: monthlyAchievementData?.Wellness_Target || 0,
      Achieved: monthlyAchievementData?.Wellness_Achieved || 0,
    },
    {
      name: "Google Rating",
      Target: monthlyAchievementData?.Google_Rating_Target,
      Achieved: monthlyAchievementData?.Google_Rating_Achieved || 0,
    },
  ];

  const monthlyData = [
    { label: "Select", value: "" },
    { label: "Jan", value: 1 },
    { label: "Feb", value: 2 },
    { label: "Mar", value: 3 },
    { label: "Apr", value: 4 },
    { label: "May", value: 5 },
    { label: "Jun", value: 6 },
    { label: "Jul", value: 7 },
    { label: "Aug", value: 8 },
    { label: "Sep", value: 9 },
    { label: "Oct", value: 10 },
    { label: "Nov", value: 11 },
    { label: "Dec", value: 12 },
  ];

  const selectedMonthLabel =
    monthlyData.find((item) => item.value === month)?.label || "Select Month";

  const kpiData = [
    {
      name: "Google Rating",
      target: monthlyAchievementData?.Google_Rating_Target || 0,
      achieved: monthlyAchievementData?.Google_Rating_Achieved || 0,
      score: monthlyAchievementData.Google_Rating_Score_Weightage || "0%",
    },
    {
      name: "Google Review",
      target: monthlyAchievementData?.Google_Review_Target || 0,
      achieved: monthlyAchievementData?.Google_Review_Achieved || 0,
      score: monthlyAchievementData?.Google_Review_Score_Weightage || "0%",
    },
    {
      name: "Overall Revenue",
      target: monthlyAchievementData?.Overall_Revenue_Target || 0,
      achieved: monthlyAchievementData?.Overall_Achieved || 0,
      score: monthlyAchievementData?.OR_Score_Weightage || "0%",
    },
    {
      name: "Wellness Revenue",
      target: monthlyAchievementData?.Wellness_Target || 0,
      achieved: monthlyAchievementData?.Wellness_Achieved || 0,
      score: monthlyAchievementData?.Wellness_Score_Weightage || "0%",
    },
  ];

  const pointsCriteria = [
    { range: "If Cumulative Score < 90%", points: 0 },
    { range: "If Cumulative Score is 91%-95%", points: 100 },
    { range: "If Cumulative Score is 96%-100%", points: 200 },
    { range: "If Cumulative Score is 101%-110%", points: 300 },
    { range: "If Cumulative Score is 111%-120%", points: 400 },
    { range: "If Cumulative Score > 120%", points: 500 },
  ];

  return (
    <CommonContainer icon={<FaRegFileAlt size={20}/>} heading="Partner Details">
      <div className="container-fluid min-vh-100 p-0">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-3 p-3 mb-4">
          <div className="row align-items-center gy-3">
            {/* Partner Info */}
            {/* <div className="col-12 col-md-8">
            <h5 className="mb-1 fw-bold">Partner name: ABC</h5>
            <small className="text-muted">
              (Your Last Login: 21-Aug-25 02:10:28)
            </small>
          </div> */}

            {/* Points + Select + DID */}
            <div className="">
              <div className="d-flex flex-column flex-lg-row gap-2">
                <div className="custom-submitbtn px-3 py-1 rounded w-100 w-sm-auto">
                  <i className="fas fa-trophy me-1"></i>
                  Your Cumulative Points (YTD): 200
                </div>
                {/* <input
                  className="form-control w-100 w-sm-auto"
                  style={{ minWidth: "100px" }}
                  list="partner-codes"
                  value={partnerList.partnerCode}
                  onChange={(e) => {
                    const partner = partnerList.find(
                      (p) => p.partnerCode === e.target.value
                    );
                    setSelectedPartner(partner || null);
                  }}
                  placeholder="Select or type Partner Code"
                />
                <datalist id="partner-codes">
                  {partnerList.map((partner, index) => (
                    <option key={index} value={partner.partnerCode} />
                  ))}
                </datalist> */}

                {/* Custom Partner Dropdown */}
                <div className="position-relative" ref={dropdownRef} style={{ minWidth: "200px" }}>
                  <div className="position-relative">
                    <input
                      ref={inputRef}
                      className="form-control w-100 w-sm-auto"
                      style={{ 
                        minWidth: "100px",
                        paddingRight: "40px",
                        cursor: "pointer"
                      }}
                      value={searchTerm || selectedPartner?.partnerCode || ''}
                      onChange={handleInputChange}
                      onFocus={() => setIsDropdownOpen(true)}
                      onKeyDown={handleKeyDown}
                      placeholder="Select or type Partner Code"
                      autoComplete="off"
                    />
                    
                    {/* Dropdown Arrow */}
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent"
                      style={{ right: "8px", zIndex: 10 }}
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      tabIndex={-1}
                    >
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </div>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div 
                      className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg"
                      style={{ 
                        zIndex: 1000,
                        maxHeight: "200px",
                        overflowY: "auto"
                      }}
                    >
                      {filteredPartners.length > 0 ? (
                        filteredPartners.map((partner, index) => (
                          <div
                            key={index}
                            className={`px-3 py-2 cursor-pointer border-bottom ${
                              selectedPartner?.partnerCode === partner.partnerCode 
                                ? 'bg-primary-custom text-white' 
                                : ''
                            }`}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => {
                              if (selectedPartner?.partnerCode !== partner.partnerCode) {
                                e.target.classList.add('bg-light');
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedPartner?.partnerCode !== partner.partnerCode) {
                                e.target.classList.remove('bg-light');
                              }
                            }}
                            onClick={() => handlePartnerSelect(partner)}
                          >
                            <div className="fw-bold">{partner.partnerCode}</div>
                            {partner.didNumber && (
                              <div className="small text-muted">DID: {partner.didNumber}</div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-muted">
                          No partners found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedPartner && selectedPartner.didNumber && (
                  <span className="bg-primary-light text-primary-custom px-2 py-1 rounded fw-bold text-nowrap w-100 w-sm-auto">
                    DID no.: {selectedPartner.didNumber}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid px-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between w-100 gap-3 mb-3">
            <div className="d-flex flex-column align-items-md-end custom-close-reportwrapper flex-md-row gap-3">
              <div className="mb-md-0">
                <label>Select Year:</label>
                <select
                  className="form-select w-100 w-md-auto"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="2025-2026">2025-2026</option>
                  <option value="2024-2025">2024-2025</option>
                </select>
              </div>

              <div className="mb-md-0">
                <label>Select Month:</label>
                <select
                  className="form-select w-100 w-md-auto"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {monthlyData.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Refresh Button */}
              <div className="metlink-report-refresh-btn-wrapper">
                <button
                  onClick={handleAchievementDetails}
                  disabled={loading || !selectedPartner}
                  className="btn custom-sumbitbtn d-flex align-items-center justify-content-center metlink-report-refresh-btn"
                  style={{ width: "40px", height: "40px" }}
                >
                  {loading ? (
                    <Loader2 />
                  ) : (
                    <RefreshCcw size={30} color="white" />
                  )}
                </button>
              </div>

              {/* <div>
              <button
                    className="custom-submitbtn rounded-1 w-100 p-2 w-md-auto"
                    onClick={() => setShowReportForm(!showReportForm)}
                >
                    {showReportForm ? "Clear Filter" : "Report"}
                </button>
              </div> */}
            </div>
            {/* {showReportForm && (
            <div className="bg-white shadow-sm p-3 rounded">
                <form>
                    <div className="row g-3 align-items-center">
                    <div className="col-12 col-md-4">
                        <label className="form-label">Report Name</label>
                        <select className="form-select">
                        <option disabled selected>Report Name</option>
                        <option>Cumulative Report</option>
                        <option>Login Report</option>
                        </select>
                    </div>

                    <div className="col-12 col-md-3">
                        <label className="form-label">From Date</label>
                        <input type="date" className="form-control" />
                    </div>

                    <div className="col-12 col-md-3">
                        <label className="form-label">To Date</label>
                        <input type="date" className="form-control" />
                    </div>

                    <div className="col-12 col-md-2  d-flex align-items-end">
                        <button type="submit" className=" p-2 rounded-1 custom-submitbtn w-100">
                        Submit
                        </button>
                    </div>
                    </div>
                </form>
            </div>
            )} */}

            <div>
              {dataLoaded && (
                <div className="text-center">
                  <div className="border border-primary-custom rounded p-3 d-inline-block w-100 w-md-auto">
                    <h6 className="text-primary-custom mb-3">
                      Performance of the month
                    </h6>
                    <div className="d-flex gap-2 justify-content-center">
                      {[1, 2, 3, 4, 5].map((star, index) => (
                        <i
                          key={index}
                          className="fas fa-star text-muted"
                          style={{ fontSize: "2rem" }}
                        ></i>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {dataLoaded ? (
            <>
              <div className="row mb-4">
                {/* Cumulative Score Section */}
                <div className="col-md-6">
                  <div className="bg-primary-light p-3 rounded mb-3">
                    <strong>Your cumulative score for this month is 0%.</strong>
                  </div>

                  {/* NPS Score Criteria */}
                  <div className="card mb-3">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">NPS Score Criteria</h6>
                    </div>
                    <div className="card-body">
                      <div className="border border-primary-custom rounded p-3 text-center">
                        <div className="fw-bold mb-2">NPS Score</div>
                        <div>If NPS Score = 85, one star will be awarded</div>
                      </div>
                    </div>
                  </div>

                  {/* Non Compliance Status */}
                  <div className="card">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">Non Compliance Status</h6>
                    </div>
                    <div className="card-body">
                      <div className="border border-primary-custom rounded p-3">
                        <div className="text-center fw-bold mb-3">
                          Non Compliance
                        </div>
                        <div className="row">
                          <div className="col-6 text-center">
                            <div className="fw-bold">Total Non Compliances</div>
                            <div className="fs-4 text-primary-custom">
                              {monthlyAchievementData.NC_Total || 0}
                            </div>
                          </div>
                          <div className="col-6 text-center">
                            <div className="fw-bold">
                              Non Compliances Fulfilled
                            </div>
                            <div className="fs-4 text-primary-custom">
                              {monthlyAchievementData.NC_Fulfilled || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Points Section */}
                <div className="col-md-6">
                  <div className="bg-primary-light p-3 rounded mb-3">
                    <strong>
                      Your cumulative points for this month is{" "}
                      <span className="text-primary-custom">0</span>
                    </strong>
                  </div>

                  {/* Cumulative Points Criteria */}
                  <div className="card">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">Cumulative Points Criteria</h6>
                    </div>
                    <div className="card-body">
                      <div className="border border-primary-custom rounded p-0">
                        <table className="table table-sm mb-0 table table-bordered custom-table">
                          <thead className="bg-light">
                            <tr>
                              <th>Points Criteria</th>
                              <th className="text-center">Points</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pointsCriteria.map((criteria, index) => (
                              <tr key={index}>
                                <td>{criteria.range}</td>
                                <td className="text-center">
                                  {criteria.points}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                {/* KPIs Score */}
                <div className="col-md-6 mb-4">
                  <div className="card">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">
                        KPIs Score for {selectedMonthLabel} {formattedYear}
                      </h6>
                    </div>
                    <div className="card-body">
                      <table className="table table-bordered table-sm table table-bordered custom-table">
                        <thead className="bg-light">
                          <tr>
                            <th>KPIs</th>
                            <th>Target</th>
                            <th>Achieved</th>
                            <th>Score</th>
                            <th>Total Score</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kpiData.map((kpi, index) => (
                            <tr key={index}>
                              <td className="fw-bold">{kpi.name}</td>
                              <td>{kpi.target}</td>
                              <td>{kpi.achieved}</td>
                              <td>{kpi.score}</td>
                              {index === 0 && (
                                <td
                                  rowSpan={4}
                                  className="text-center align-middle fs-1 fw-bold"
                                >
                                  0%
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* MTD OR Achievements */}
                  <div className="card mt-3">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">
                        MTD OR Achievements for {selectedMonthLabel} {formattedYear}
                      </h6>
                    </div>
                    <div className="card-body">
                      <table className="table table-bordered table-sm table table-bordered custom-table">
                        <thead className="bg-light">
                          <tr>
                            <th>Day</th>
                            <th>OR Target</th>
                            <th>OR Achieved</th>
                            <th>OR Landing</th>
                            <th>OR Achievement Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{day}</td>
                            <td>{mtdAchievementData.Or_Target}</td>
                            <td>{mtdAchievementData.Or_Achivement}</td>
                            <td>{mtdAchievementData.Or_Landing}</td>
                            <td>
                              {mtdAchievementData.Or_Achivement_Percentage}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* MTD WR Achievements */}
                  <div className="card mt-3">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">
                        MTD WR Achievements for {selectedMonthLabel} {formattedYear}
                      </h6>
                    </div>
                    <div className="card-body">
                      <table className="table table-bordered table-sm table table-bordered custom-table">
                        <thead className="bg-light">
                          <tr>
                            <th>Day</th>
                            <th>WR Target</th>
                            <th>WR Achieved</th>
                            <th>WR Landing</th>
                            <th>WR Achievement Percentage</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{day}</td>
                            <td>{mtdAchievementData.Wr_Target}</td>
                            <td>{mtdAchievementData.Wr_Achivement}</td>
                            <td>{mtdAchievementData.Wr_Landing}</td>
                            <td>
                              {mtdAchievementData.Wr_Achivement_Percentage}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Overall Performance Summary */}
                <div className="col-md-6 mb-4">
                  <div className="card h-100">
                    <div className="metlink-card-header bg-primary-custom text-white text-center">
                      <h6 className="mb-0">
                        Overall Performance Summary for {selectedMonthLabel} {formattedYear}
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Monthly Target & Achieve Chart */}
                      <div className="mb-5 pb-3" style={{ height: "320px" }}>
                        <p className="text-center text-muted mb-2">
                          Monthly Target & Achieved
                        </p>

                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={performanceData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="name"
                              tick={{
                                fontSize: window.innerWidth < 576 ? 10 : 12,
                              }}
                            />
                            <YAxis
                              tick={{
                                fontSize: window.innerWidth < 576 ? 10 : 12,
                              }}
                            />
                            <Tooltip />
                            <Legend
                              wrapperStyle={{
                                fontSize:
                                  window.innerWidth < 576 ? "10px" : "12px",
                              }}
                            />
                            <Bar dataKey="Target" fill="#28a745">
                              <LabelList
                                dataKey="Target"
                                position="top"
                                formatter={(val) => val.toFixed(2)}
                                style={{
                                  fontSize:
                                    window.innerWidth < 576 ? "10px" : "12px",
                                }}
                              />
                            </Bar>
                            <Bar dataKey="Achieved" fill="#8884d8">
                              <LabelList
                                dataKey="Achieved"
                                position="top"
                                formatter={(val) => val.toFixed(2)}
                                style={{
                                  fontSize:
                                    window.innerWidth < 576 ? "10px" : "12px",
                                }}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Scope of Improvement */}
                      <div className="bg-primary-light rounded p-3">
                        <h6 className="text-center mb-3">
                          Scope of Improvement:
                        </h6>
                        <div className="d-flex flex-wrap gap-2 justify-content-center">
                          <span className="badge bg-light text-dark border border-primary-custom">
                            Wellness
                          </span>
                          <span className="badge bg-light text-dark border border-primary-custom">
                            Overall Revenue
                          </span>
                          <span className="badge bg-light text-dark border border-primary-custom">
                            Google Rating
                          </span>
                          <span className="badge bg-light text-dark border border-primary-custom">
                            Google Reviews
                          </span>
                          <span className="badge bg-light text-dark border border-primary-custom">
                            NPS Score
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center mt-5">
              <p className="text-muted fs-5">
                Please select <strong>Partner Code</strong>,{" "}
                <strong>Year</strong>, and <strong>Month</strong> to view the
                report.
              </p>
            </div>
          )}
        </div>

        <style jsx>{`
          :root {
            --color-primary: #28a745;
            --color-white: #ffffff;
            --color-primary-light: #d4edda;
            --color-primary-dark: #1e7e34;
          }

          .min-vh-100 {
            min-height: 100vh;
          }
          .table th,
          .table td {
            font-size: 0.875rem;
          }
          .btn-group .btn {
            border-radius: 0;
          }
          .btn-group .btn:first-child {
            border-top-left-radius: 0.375rem;
            border-bottom-left-radius: 0.375rem;
          }
          .btn-group .btn:last-child {
            border-top-right-radius: 0.375rem;
            border-bottom-right-radius: 0.375rem;
          }
          .metlink-card-header h6 {
            font-weight: 600;
          }

          .custom-submitbtn {
            background-color: var(--color-primary) !important;
            color: var(--color-white) !important;
            border: none !important;
            transition: all 0.3s ease !important;
          }

          .bg-primary-custom {
            background-color: var(--color-primary) !important;
          }

          .bg-primary-light {
            background-color: var(--color-primary-light) !important;
          }

          .bg-primary-dark {
            background-color: var(--color-primary-dark) !important;
          }

          .text-primary-custom {
            color: var(--color-primary) !important;
          }

          .border-primary-custom {
            border-color: var(--color-primary) !important;
          }

          .btn-primary-custom {
            background-color: var(--color-primary);
            color: var(--color-white);
            border: 1px solid var(--color-primary);
            transition: all 0.3s ease;
          }

          .btn-primary-custom:hover {
            background-color: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }

          .btn-primary-light {
            background-color: var(--color-primary-light);
            color: var(--color-primary-dark);
            border: 1px solid var(--color-primary);
          }

          .btn-primary-light:hover {
            background-color: var(--color-primary);
            color: var(--color-white);
          }
        `}</style>
      </div>
    </CommonContainer>
  );
};

export default MetlinkReportComponent;
